import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import api from '../lib/api'

export interface AppSettings {
  // Branding
  companyLogo: string | null
  brandName: string
  proposalLinkPrefix: string
  defaultFont: string
  signatureButtonColor: string
  buttonTextColor: string

  // Proposals
  proposalLanguage: string
  defaultCurrency: string
  proposalExpiryDays: number
  priceSeparator: string
  dateFormat: string
  decimalPrecision: number

  // Contact Buttons
  emailEnabled: boolean
  emailAddress: string
  phoneEnabled: boolean
  phoneNumber: string
  whatsappEnabled: boolean
  whatsappNumber: string

  // Redirect
  redirectUrl: string
  redirectDelay: number

  // Features
  enableDownloadPreview: boolean
  sendToTeammate: string
  aiAssistantEnabled: boolean

  // Signatures
  yourSignatureType: string
  prospectSignatureOptions: string
  savedSignature: string | null

  // Project Types
  projectTypes: string[]

  // PDF Options
  showPdfFooter: boolean
  pdfFooterTitle: string
  pdfFooterContent: string
}

const defaultSettings: AppSettings = {
  companyLogo: null,
  brandName: '',
  proposalLinkPrefix: '',
  defaultFont: 'Heebo',
  signatureButtonColor: '#FF69B4',
  buttonTextColor: '#ffffff',

  proposalLanguage: 'he',
  defaultCurrency: 'ILS',
  proposalExpiryDays: 7,
  priceSeparator: 'comma',
  dateFormat: 'DD/MM/YYYY',
  decimalPrecision: 2,

  emailEnabled: true,
  emailAddress: '',
  phoneEnabled: true,
  phoneNumber: '',
  whatsappEnabled: true,
  whatsappNumber: '',

  redirectUrl: '',
  redirectDelay: 2,

  enableDownloadPreview: true,
  sendToTeammate: '',
  aiAssistantEnabled: true,

  yourSignatureType: 'draw',
  prospectSignatureOptions: 'draw',
  savedSignature: null,

  projectTypes: ['design', 'development'],

  showPdfFooter: false,
  pdfFooterTitle: '',
  pdfFooterContent: ''
}

interface SettingsContextType {
  settings: AppSettings
  updateSetting: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => void
  updateSettings: (newSettings: Partial<AppSettings>) => void
  resetSettings: () => void
  saveSettings: () => Promise<void>
  loading: boolean
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

const STORAGE_KEY = 'quor_settings'

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [settings, setSettings] = useState<AppSettings>(defaultSettings)
  const [loading, setLoading] = useState(true)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loadedFromApiRef = useRef(false)

  // Load settings: try API first (if logged in), fallback to localStorage
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true)
      loadedFromApiRef.current = false

      // Always start with localStorage cache for fast load
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored)
          setSettings({ ...defaultSettings, ...parsed })
        }
      } catch {
        // ignore localStorage errors
      }

      // If logged in, fetch from API (may override localStorage)
      if (user) {
        try {
          const data = await api.getSettings()
          if (data.settings && Object.keys(data.settings).length > 0) {
            const merged = { ...defaultSettings, ...data.settings } as AppSettings
            setSettings(merged)
            // Update localStorage cache
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
            loadedFromApiRef.current = true
          } else {
            // API returned empty settings - push localStorage settings to API
            const stored = localStorage.getItem(STORAGE_KEY)
            if (stored) {
              const parsed = JSON.parse(stored)
              if (Object.keys(parsed).length > 0) {
                api.saveSettings(parsed).catch(() => {})
              }
            }
            loadedFromApiRef.current = true
          }
        } catch {
          // API failed - keep localStorage settings
        }
      }

      setLoading(false)
    }

    loadSettings()
  }, [user])

  // Debounced save to API + localStorage
  const debouncedSave = useCallback((newSettings: AppSettings) => {
    // Always save to localStorage immediately
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings))
    } catch {
      // ignore
    }

    // Debounce API save to avoid too many requests
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }

    if (user) {
      saveTimerRef.current = setTimeout(() => {
        api.saveSettings(newSettings as unknown as Record<string, unknown>).catch(() => {})
      }, 1000) // 1 second debounce
    }
  }, [user])

  const updateSetting = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value }
      debouncedSave(next)
      return next
    })
  }

  const updateSettings = (newSettings: Partial<AppSettings>) => {
    setSettings(prev => {
      const next = { ...prev, ...newSettings }
      debouncedSave(next)
      return next
    })
  }

  const resetSettings = () => {
    setSettings(defaultSettings)
    localStorage.removeItem(STORAGE_KEY)
    if (user) {
      api.saveSettings({}).catch(() => {})
    }
  }

  const saveSettings = async () => {
    // Explicit save - flush immediately
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
      if (user) {
        await api.saveSettings(settings as unknown as Record<string, unknown>)
      }
    } catch (error) {
      console.error('Failed to save settings:', error)
      throw error
    }
  }

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSetting,
      updateSettings,
      resetSettings,
      saveSettings,
      loading
    }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}

// Helper functions for formatting based on settings
export function formatPrice(amount: number, settings: AppSettings): string {
  const { defaultCurrency, priceSeparator, decimalPrecision } = settings

  const currencySymbols: Record<string, string> = {
    ILS: '₪',
    USD: '$',
    EUR: '€'
  }

  const symbol = currencySymbols[defaultCurrency] || defaultCurrency

  const formatted = amount.toFixed(decimalPrecision)
  const [intPart, decPart] = formatted.split('.')

  // Add thousand separators
  const separator = priceSeparator === 'comma' ? ',' : '.'
  const decimalSep = priceSeparator === 'comma' ? '.' : ','

  const withSeparators = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator)

  return `${symbol}${withSeparators}${decPart ? decimalSep + decPart : ''}`
}

export function formatDate(date: Date | string, settings: AppSettings): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const { dateFormat } = settings

  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()

  switch (dateFormat) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`
    default:
      return `${day}/${month}/${year}`
  }
}
